import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  IconButton,
  Typography,
  Paper,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

const StepEditor = ({ steps: initialSteps = [], onSave, parts = [] }) => {
  const [steps, setSteps] = useState([]);
  const [expandedSteps, setExpandedSteps] = useState({});

  const cleanStep = useCallback((step) => {
    // Create a new object without empty properties
    const cleanedStep = Object.entries(step).reduce((acc, [key, value]) => {
      // Skip empty strings, null, undefined, empty arrays, and repeat value of 0
      if (value === '' || value === null || value === undefined || 
          (Array.isArray(value) && value.length === 0) ||
          (key === 'repeat' && value === 0)) {
        return acc;
      }
      // Recursively clean nested steps
      if (key === 'steps' && Array.isArray(value)) {
        const cleanedSteps = value.map(cleanStep);
        if (cleanedSteps.length > 0) {
          acc[key] = cleanedSteps;
        }
      } else {
        acc[key] = value;
      }
      return acc;
    }, {});
    
    return cleanedStep;
  }, []);

  const normalizeSteps = useCallback((steps) => {
    if (!Array.isArray(steps)) return [];
    
    return steps.map(step => {
      // Remove the ID requirement - we'll generate IDs based on index
      const normalizedStep = { ...step };
      // Remove any existing ID since we'll use index-based IDs
      delete normalizedStep.id;
      
      // Only add steps array if it exists in the original step
      if (step.steps) {
        normalizedStep.steps = normalizeSteps(step.steps);
      }
      return normalizedStep;
    });
  }, []);

  useEffect(() => {
    // Normalize steps and ensure all have IDs
    const normalizedSteps = normalizeSteps(initialSteps);
    setSteps(normalizedSteps);
    
    // Initialize all steps as collapsed
    const initialExpanded = {};
    const setExpandedState = (steps, pathPrefix = '') => {
      steps.forEach((step, index) => {
        const stepId = pathPrefix ? `${pathPrefix}_${index}` : `step_${index}`;
        initialExpanded[stepId] = false;
        if (step.steps && step.steps.length > 0) {
          setExpandedState(step.steps, stepId);
        }
      });
    };
    setExpandedState(normalizedSteps);
    setExpandedSteps(initialExpanded);
  }, [initialSteps, normalizeSteps]);

  const toggleStep = (stepId) => {
    setExpandedSteps(prev => ({
      ...prev,
      [stepId]: !prev[stepId]
    }));
  };

  const handleAddStep = (parentStepId = null) => {
    const newStep = {
      title: '',
      description: '',
      name: '',
      verbal: '',
      note: '',
      endnote: '',
      yarn: '',
      hook: '',
      needles: '',
      facing: '',
      repeat: 0,
      part_id: '',
      steps: [],
    };

    if (parentStepId === null) {
      // Adding a root level step
      const newStepIndex = steps.length;
      const newStepId = `step_${newStepIndex}`;
      
      // Set the new step as expanded
      setExpandedSteps(prev => ({
        ...prev,
        [newStepId]: true
      }));
      
      setSteps(prevSteps => [...prevSteps, newStep]);
    } else {
      // Adding a sub-step - need to find the parent step and add to its steps array
      const updateSteps = (steps, pathPrefix = '') => {
        return steps.map((step, index) => {
          const stepId = pathPrefix ? `${pathPrefix}_${index}` : `step_${index}`;
          
          if (stepId === parentStepId) {
            const newSubStepIndex = (step.steps || []).length;
            const newSubStepId = `${stepId}_${newSubStepIndex}`;
            
            // Set the new sub-step as expanded
            setExpandedSteps(prev => ({
              ...prev,
              [newSubStepId]: true
            }));
            
            return {
              ...step,
              steps: [...(step.steps || []), newStep]
            };
          }
          
          if (step.steps) {
            return {
              ...step,
              steps: updateSteps(step.steps, stepId)
            };
          }
          return step;
        });
      };
      setSteps(prevSteps => updateSteps(prevSteps));
    }
  };

  const handleDeleteStep = (stepIdToDelete) => {
    const deleteStep = (steps, pathPrefix = '') => {
      return steps.filter((step, index) => {
        const stepId = pathPrefix ? `${pathPrefix}_${index}` : `step_${index}`;
        if (stepId === stepIdToDelete) return false;
        return true;
      }).map((step, index) => {
        const stepId = pathPrefix ? `${pathPrefix}_${index}` : `step_${index}`;
        if (step.steps) {
          return {
            ...step,
            steps: deleteStep(step.steps, stepId)
          };
        }
        return step;
      });
    };
    setSteps(prevSteps => deleteStep(prevSteps));
    
    // Remove from expanded steps
    setExpandedSteps(prev => {
      const newExpanded = { ...prev };
      delete newExpanded[stepIdToDelete];
      return newExpanded;
    });
  };

  const handleUpdateStep = (stepIdToUpdate, field, value) => {
    const updateStep = (steps, pathPrefix = '') => {
      return steps.map((step, index) => {
        const stepId = pathPrefix ? `${pathPrefix}_${index}` : `step_${index}`;
        
        if (stepId === stepIdToUpdate) {
          return { ...step, [field]: value };
        }
        
        if (step.steps) {
          return {
            ...step,
            steps: updateStep(step.steps, stepId)
          };
        }
        return step;
      });
    };
    setSteps(prevSteps => updateStep(prevSteps));
  };

  const handleSave = () => {
    // Clean all steps before saving
    const cleanedSteps = steps.map(cleanStep);
    onSave(cleanedSteps);
  };

  const renderStep = (step, index, pathPrefix = '') => {
    if (!step) return null;

    const stepId = pathPrefix ? `${pathPrefix}_${index}` : `step_${index}`;
    const isExpanded = expandedSteps[stepId];
    
    // Get the display title, preferring title over name, falling back to step number
    const displayTitle = step.title || step.name || `Step ${index + 1}`;

    return (
      <Paper
        key={`step-${stepId}`}
        sx={{
          p: 1.5,
          mb: 1,
          backgroundColor: 'white',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box display="flex" alignItems="center" mb={1}>
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            {displayTitle}
          </Typography>
          <IconButton size="small" onClick={() => toggleStep(stepId)}>
            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
          <IconButton size="small" onClick={() => handleDeleteStep(stepId)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>

        <Collapse in={isExpanded}>
          <Stack spacing={1}>
            <TextField
              label="Title"
              value={step.title || ''}
              onChange={(e) => handleUpdateStep(stepId, 'title', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Name"
              value={step.name || ''}
              onChange={(e) => handleUpdateStep(stepId, 'name', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Description"
              value={step.description || ''}
              onChange={(e) => handleUpdateStep(stepId, 'description', e.target.value)}
              multiline
              rows={2}
              sx={{ '& textarea': { resize: 'both' } }}
              fullWidth
              size="small"
            />
            <TextField
              label="Note"
              value={step.note || ''}
              onChange={(e) => handleUpdateStep(stepId, 'note', e.target.value)}
              multiline
              rows={2}
              sx={{ '& textarea': { resize: 'both' } }}
              fullWidth
              size="small"
            />
            <TextField
              label="Verbal Instructions"
              value={step.verbal || ''}
              onChange={(e) => handleUpdateStep(stepId, 'verbal', e.target.value)}
              multiline
              rows={2}
              sx={{ '& textarea': { resize: 'both' } }}
              fullWidth
              size="small"
            />
            <TextField
              label="End Note"
              value={step.endnote || ''}
              onChange={(e) => handleUpdateStep(stepId, 'endnote', e.target.value)}
              multiline
              rows={2}
              sx={{ '& textarea': { resize: 'both' } }}
              fullWidth
              size="small"
            />
            <Box display="flex" gap={1}>
              <TextField
                label="Yarn"
                value={step.yarn || ''}
                onChange={(e) => handleUpdateStep(stepId, 'yarn', e.target.value)}
                sx={{ flex: 1 }}
                size="small"
              />
              <TextField
                label="Hook"
                value={step.hook || ''}
                onChange={(e) => handleUpdateStep(stepId, 'hook', e.target.value)}
                sx={{ flex: 1 }}
                size="small"
              />
              <TextField
                label="Needles"
                value={step.needles || ''}
                onChange={(e) => handleUpdateStep(stepId, 'needles', e.target.value)}
                sx={{ flex: 1 }}
                size="small"
              />
            </Box>
            <Box display="flex" gap={1}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Facing</InputLabel>
                <Select
                  value={step.facing || ''}
                  onChange={(e) => handleUpdateStep(stepId, 'facing', e.target.value)}
                  label="Facing"
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="RS">Right Side</MenuItem>
                  <MenuItem value="WS">Wrong Side</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Repeat"
                type="number"
                value={step.repeat || 0}
                onChange={(e) => handleUpdateStep(stepId, 'repeat', parseInt(e.target.value) || 0)}
                sx={{ flex: 1 }}
                size="small"
              />
            </Box>

            <FormControl size="small" fullWidth>
              <InputLabel>Part</InputLabel>
              <Select
                value={step.part_id || ''}
                onChange={(e) => handleUpdateStep(stepId, 'part_id', e.target.value)}
                label="Part"
              >
                <MenuItem value="">None</MenuItem>
                {Object.entries(parts).map(([id, part]) => (
                  <MenuItem key={id} value={id}>
                    {part.name || `Part ${id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Box mt={1}>
            <Button
              startIcon={<AddIcon />}
              onClick={() => handleAddStep(stepId)}
              variant="outlined"
              size="small"
            >
              Add Sub-step
            </Button>
          </Box>

          {step.steps && step.steps.length > 0 && (
            <Box sx={{ mt: 1, pl: 2 }}>
              {step.steps.map((subStep, subIndex) =>
                renderStep(subStep, subIndex, stepId)
              )}
            </Box>
          )}
        </Collapse>
      </Paper>
    );
  };

  return (
    <Box>
      <Box>
        {steps.map((step, index) => renderStep(step, index))}
      </Box>

      <Box mt={1} display="flex" gap={1}>
        <Button
          startIcon={<AddIcon />}
          onClick={() => handleAddStep()}
          variant="contained"
          size="small"
        >
          Add Step
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          size="small"
        >
          Save Changes
        </Button>
      </Box>
    </Box>
  );
};

export default StepEditor; 